import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHotCards, type HotCardItem } from '../api/cards';
import { hotCardsMock } from '../mocks/hotCards';

export default function HotCards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState<HotCardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // 防止 StrictMode 下重复调用
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchHotCards = async () => {
      try {
        setIsLoading(true);
        const response = await getHotCards(8, 7);
        if (response.code === 200 && response.data.length > 0) {
          setCards(response.data);
        } else {
          // 如果没有数据，使用 mock 数据作为后备
          setCards(hotCardsMock.map((m) => ({
            id: m.id,
            name: m.name,
            image: m.image,
            number: m.number || '',
            transactionCount: 0,
            avgPrice: 0,
            currency: 'JPY',
          })));
        }
      } catch (err) {
        console.error('获取热门卡片失败:', err);
        // 使用 mock 数据作为后备
        setCards(hotCardsMock.map((m) => ({
          id: m.id,
          name: m.name,
          image: m.image,
          number: m.number || '',
          transactionCount: 0,
          avgPrice: 0,
          currency: 'JPY',
        })));
      } finally {
        setIsLoading(false);
      }
    };

    fetchHotCards();
  }, []);

  const handleCardClick = (card: HotCardItem) => {
    navigate(`/card/${card.id}`, {
      state: {
        card: {
          uid: card.id,
          name: card.name,
          number: card.number,
          image_url_at_self_platform: card.image,
        },
      },
    });
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-lg font-semibold text-primary mb-4">热门卡片</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white border border-border rounded-[16px] overflow-hidden animate-pulse">
              <div className="aspect-[63/88] bg-gray-200" />
              <div className="p-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-primary">热门卡片</h2>
        <span className="text-xs text-secondary">近7天成交量排行</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card)}
            className="bg-white border border-border rounded-[16px] overflow-hidden hover:shadow-md hover:border-primary transition-all text-left"
          >
            {/* 卡片图片 */}
            <div className="aspect-[63/88] bg-background-secondary overflow-hidden relative p-1">
              {card.image ? (
                <img
                  src={card.image}
                  alt={card.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-secondary-light">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
              {/* 成交量标签 */}
              {card.transactionCount > 0 && (
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                  {card.transactionCount} 笔
                </div>
              )}
            </div>

            {/* 卡片信息 */}
            <div className="p-3">
              <h3 className="text-sm font-medium text-primary truncate">{card.name}</h3>
              {card.number && (
                <p className="text-xs text-secondary-light mt-1 truncate">{card.number}</p>
              )}
              {card.avgPrice > 0 && (
                <p className="text-xs text-secondary mt-1">
                  均价: <span className="font-medium text-primary">{card.currency} {card.avgPrice.toLocaleString()}</span>
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
